import { useCallback, useMemo } from 'react'

import * as R from 'ramda'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { User } from '@driftt/api-types'

import { fetchUserProperties, patchUserProperties } from 'api/customer'
import * as UserSelectors from 'modules/user/selectors'

import { findKeyByName } from './FirstExperienceModal.helpers'

export const useFirstExperienceProperties = (
  propertyName: string,
  featureCreatedDate: Date
) => {
  const currentUser: User | undefined = useSelector(UserSelectors.currentUser)
  const userId = R.path(['id'], currentUser)
  const userCreatedBeforeFeatureCreated =
    R.pathOr(new Date().getTime(), ['createdAt'], currentUser) <
    featureCreatedDate.getTime()

  const {
    data: properties = {},
    mutate,
    isValidating,
  } = useSWR(
    [`/users/${userId}/properties`, userCreatedBeforeFeatureCreated],
    fetchUserProperties,
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
      dedupingInterval: 60000,
    }
  )
  const hasDismissedProperty = useMemo(
    () => findKeyByName(properties, propertyName),
    [properties, propertyName]
  )

  const patchProperty = useCallback(
    async () =>
      patchUserProperties(userId, { [propertyName]: true })
        .then(({ data }) => {
          mutate(data)
        })
        .catch((err) => console.error(err)),
    []
  )

  const shouldShowFirstExperience =
    userCreatedBeforeFeatureCreated && !isValidating && !hasDismissedProperty

  return useMemo(
    () => ({ shouldShowFirstExperience, patchProperty }),
    [shouldShowFirstExperience, patchProperty]
  )
}
